import { useEffect, useState } from "react";
import { Phone, Building2, FileText, HardHat, CheckCircle2, FolderCheck } from "lucide-react";
import { LogoMark } from "./Logo";

const STEPS = [
    { num: 1, title: "Kunde", icon: Phone, desc: "Kunden kontakter virksomheden via telefon, webformular eller chat.", tone: "blue" },
    { num: 2, title: "MyServiceHelper / Kontor", icon: Building2, desc: "Kunden oprettes, opgaven registreres, og kontoret får samlet overblik.", tone: "indigo" },
    { num: 3, title: "Data på opgaven", icon: FileText, desc: "Adresse, prioritet, materialer, noter og tidspunkt samles ét sted.", tone: "purple" },
    { num: 4, title: "Medarbejder", icon: HardHat, desc: "Medarbejderen modtager opgaven, udfører arbejdet og sender opdateringer.", tone: "blue" },
    { num: 5, title: "Færdig", icon: CheckCircle2, desc: "Opgaven afsluttes, og tid, materialer og noter registreres.", tone: "emerald" },
    { num: 6, title: "Klar til faktura", icon: FolderCheck, desc: "Dokumentation og fakturagrundlag samles automatisk.", tone: "indigo" },
];

const TONE = {
    blue: { badge: "bg-blue-100 text-blue-700", iconWrap: "bg-blue-50 text-blue-600 ring-blue-100" },
    indigo: { badge: "bg-indigo-100 text-indigo-700", iconWrap: "bg-indigo-50 text-indigo-600 ring-indigo-100" },
    purple: { badge: "bg-purple-100 text-purple-700", iconWrap: "bg-purple-50 text-purple-600 ring-purple-100" },
    emerald: { badge: "bg-emerald-100 text-emerald-700", iconWrap: "bg-emerald-50 text-emerald-600 ring-emerald-100" },
};

function StepCard({ step, active, className = "" }) {
    const Icon = step.icon;
    const t = TONE[step.tone];
    return (
        <div
            data-testid={`workflow-step-${step.num}`}
            data-active={active ? "true" : "false"}
            className={`bg-white rounded-xl p-3 transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] ${
                active
                    ? "border-2 border-blue-500 shadow-xl shadow-blue-500/25 -translate-y-1 scale-[1.03] ring-2 ring-blue-200/60"
                    : "border border-slate-200/70 shadow-md shadow-slate-900/[0.04]"
            } ${className}`}
        >
            <div className="flex items-center gap-2 mb-1.5">
                <span
                    className={`shrink-0 w-7 h-7 rounded-full inline-flex items-center justify-center text-[11px] font-bold transition-colors duration-700 ${
                        active ? "bg-blue-600 text-white" : t.badge
                    }`}
                >
                    {step.num}
                </span>
                <span className="text-[9px] font-semibold uppercase tracking-wider text-slate-400">Trin {step.num}</span>
            </div>
            <h4 className="text-[13px] font-semibold text-slate-900 leading-tight">{step.title}</h4>
            <div className="mt-1.5 flex items-start gap-1.5">
                <span className={`shrink-0 w-6 h-6 rounded-md ring-1 inline-flex items-center justify-center ${t.iconWrap}`}>
                    <Icon className="w-3 h-3" />
                </span>
                <p className="text-[10.5px] text-slate-500 leading-snug">{step.desc}</p>
            </div>
        </div>
    );
}

function StatusCard({ compact = false, className = "" }) {
    return (
        <div data-testid="status-card" className={`bg-white rounded-xl shadow-lg shadow-slate-900/5 border border-slate-200/70 p-3 ${compact ? "w-full" : "w-[150px]"} ${className}`}>
            <div className="flex items-center justify-between mb-2">
                <span className="text-[9.5px] font-semibold uppercase tracking-wider text-slate-400">Status efter job</span>
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            </div>
            <ul className="space-y-1 text-[11.5px]">
                <li className="flex justify-between"><span className="text-slate-500">Status</span><span className="font-medium text-emerald-600">Færdig</span></li>
                <li className="flex justify-between"><span className="text-slate-500">Tid</span><span className="font-medium text-slate-900">2t 15m</span></li>
                <li className="flex justify-between"><span className="text-slate-500">Materialer</span><span className="font-medium text-slate-900">1.248 kr.</span></li>
                <li className="flex justify-between"><span className="text-slate-500">Noter</span><span className="font-medium text-slate-900">1</span></li>
            </ul>
            <div className="mt-2 pt-2 border-t border-slate-100 flex items-center gap-1.5 text-[11px] font-medium text-emerald-600">
                <CheckCircle2 className="w-3 h-3" />
                Data sendt
            </div>
        </div>
    );
}

/*
  PERFECT CIRCLE LAYOUT:
    Container: 580 × 650
    Center: cx=290, cy=325
    Radius (card centers): r=235  (cards at 60° intervals)
    Card: 150 × ~140

    Step centers (angle from -90° clockwise, +60° per step):
      Trin 1: (290,    90)   -> top-left (215, 20)
      Trin 2: (493.5, 207.5) -> top-left (419, 138)
      Trin 3: (493.5, 442.5) -> top-left (419, 373)
      Trin 4: (290,   560)   -> top-left (215, 490)
      Trin 5: (86.5,  442.5) -> top-left (12, 373)
      Trin 6: (86.5,  207.5) -> top-left (12, 138)

    Arrows: smooth Q-curves between adjacent cards, bulging outward
    along the imaginary circle so the flow feels continuous.
*/
/*
  Six short, evenly-spaced arrows placed at the midpoints between
  adjacent cards on the same circle (radius 235, 60° apart).
  Each arrow is a 16-px line tangent to the circle, clockwise.

  Midpoint angles between cards: -60°, 0°, 60°, 120°, 180°, 240°
  Midpoint positions on circle r=235, center (290, 325):
    -60°: (407.5, 121.5)   1→2
       0°: (525, 325)      2→3
      60°: (407.5, 528.5)  3→4
     120°: (172.5, 528.5)  4→5
     180°: (55, 325)       5→6
     240°: (172.5, 121.5)  6→1

  Tangent (clockwise) direction at angle θ: (-sin θ, cos θ).
  Arrow line: midpoint ± 8 * tangent.
*/
const ARROWS = [
    { id: 0, x1: 397.1, y1: 115.5, x2: 417.9, y2: 127.5 },   // 1 → 2  (len ~24)
    { id: 1, x1: 525.0, y1: 313.0, x2: 525.0, y2: 337.0 },   // 2 → 3
    { id: 2, x1: 417.9, y1: 522.5, x2: 397.1, y2: 534.5 },   // 3 → 4
    { id: 3, x1: 182.9, y1: 534.5, x2: 162.1, y2: 522.5 },   // 4 → 5
    { id: 4, x1: 55.0,  y1: 337.0, x2: 55.0,  y2: 313.0 },   // 5 → 6
    { id: 5, x1: 162.1, y1: 127.5, x2: 182.9, y2: 115.5 },   // 6 → 1
];

function ArrowsOverlay({ activeStep }) {
    return (
        <svg
            aria-hidden="true"
            className="absolute inset-0 w-full h-full pointer-events-none"
            viewBox="0 0 580 650"
            preserveAspectRatio="xMidYMid meet"
        >
            <defs>
                <marker id="wf-arrow-idle" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="5" markerHeight="5" orient="auto-start-reverse">
                    <path d="M0,1 L8,5 L0,9 z" fill="#3b82f6" />
                </marker>
                <marker id="wf-arrow-active" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                    <path d="M0,1 L8,5 L0,9 z" fill="#2563eb" />
                </marker>
            </defs>
            {ARROWS.map((arr) => {
                const isActive = arr.id === activeStep;
                return (
                    <line
                        key={arr.id}
                        x1={arr.x1}
                        y1={arr.y1}
                        x2={arr.x2}
                        y2={arr.y2}
                        stroke={isActive ? "#2563eb" : "#3b82f6"}
                        strokeWidth={isActive ? 3 : 2.2}
                        strokeLinecap="round"
                        opacity={isActive ? 1 : 0.65}
                        markerEnd={isActive ? "url(#wf-arrow-active)" : "url(#wf-arrow-idle)"}
                        style={{
                            filter: isActive ? "drop-shadow(0 0 6px rgba(37,99,235,0.55))" : "none",
                            transition: "stroke 800ms ease, stroke-width 800ms ease, opacity 800ms ease, filter 800ms ease",
                        }}
                    />
                );
            })}
        </svg>
    );
}

export default function StepByStepWorkflow() {
    const [activeStep, setActiveStep] = useState(0);

    useEffect(() => {
        const interval = setInterval(() => {
            setActiveStep((prev) => (prev + 1) % STEPS.length);
        }, 2800);
        return () => clearInterval(interval);
    }, []);

    return (
        <div data-testid="step-workflow" className="relative w-full">
            {/* Top bar: badge centered, status card top-right */}
            <div className="relative mb-4 min-h-[40px]">
                <div className="flex justify-center">
                    <span
                        data-testid="trin-badge"
                        className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white text-slate-700 text-[11px] font-semibold uppercase tracking-wider border border-slate-200 shadow-sm"
                    >
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                        Trin for trin
                    </span>
                </div>
            </div>

            {/* DESKTOP: perfect circle */}
            <div className="hidden md:block">
                <div className="relative w-full max-w-[580px] mx-auto h-[650px]">
                    {/* Outer faint big circle behind logo */}
                    <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[470px] h-[470px] rounded-full bg-gradient-to-br from-blue-100/55 via-indigo-100/35 to-purple-100/25 blur-3xl z-0" />
                    {/* Soft inner ring */}
                    <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[330px] h-[330px] rounded-full border border-blue-100/60 bg-white/30 z-0" />

                    {/* Arrows overlay */}
                    <div className="absolute inset-0 z-10">
                        <ArrowsOverlay activeStep={activeStep} />
                    </div>

                    {/* Status card - placed above container in top-right area, outside the circle */}
                    <StatusCard className="absolute -top-12 -right-2 z-30" />

                    {/* M-logo card (square white card with soft shadow) */}
                    <div
                        data-testid="m-logo-card"
                        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-20 w-[140px] h-[140px] rounded-2xl bg-white border border-slate-200/70 shadow-xl shadow-slate-900/[0.08] flex items-center justify-center"
                    >
                        <LogoMark size={92} animated className="drop-shadow-md" />
                    </div>

                    {/* Trin 1 - top */}
                    <StepCard step={STEPS[0]} active={activeStep === 0} className="absolute top-[20px] left-1/2 -translate-x-1/2 w-[150px] z-20" />
                    {/* Trin 2 - upper right (perfect circle position) */}
                    <StepCard step={STEPS[1]} active={activeStep === 1} className="absolute top-[138px] right-[11px] w-[150px] z-20" />
                    {/* Trin 3 - lower right */}
                    <StepCard step={STEPS[2]} active={activeStep === 2} className="absolute top-[373px] right-[11px] w-[150px] z-20" />
                    {/* Trin 4 - bottom */}
                    <StepCard step={STEPS[3]} active={activeStep === 3} className="absolute top-[490px] left-1/2 -translate-x-1/2 w-[150px] z-20" />
                    {/* Trin 5 - lower left (nudged slightly right for better balance) */}
                    <StepCard step={STEPS[4]} active={activeStep === 4} className="absolute top-[373px] left-[36px] w-[150px] z-20" />
                    {/* Trin 6 - upper left */}
                    <StepCard step={STEPS[5]} active={activeStep === 5} className="absolute top-[138px] left-[11px] w-[150px] z-20" />
                </div>
            </div>

            {/* MOBILE vertical stack */}
            <div className="md:hidden space-y-3">
                {STEPS.map((step, i) => (
                    <div key={step.num}>
                        <StepCard step={step} active={activeStep === i} className="w-full" />
                        {i < STEPS.length - 1 && (
                            <div
                                className={`flex justify-center py-1 transition-colors duration-700 ${
                                    activeStep === i ? "text-blue-600" : "text-blue-400"
                                }`}
                                aria-hidden="true"
                            >
                                <svg className="w-3 h-4" viewBox="0 0 16 24" fill="none">
                                    <path d="M8 0 V20 M2 14 L8 22 L14 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </div>
                        )}
                    </div>
                ))}
                <div className="pt-2">
                    <StatusCard compact />
                </div>
            </div>
        </div>
    );
}
